/**
 * One file's details, read-only, from the audit side.
 *
 * What the file is and how its rows were classified — for an auditor checking
 * where an audit's rows came from. No intake actions: mapping, retrying,
 * excluding or replacing a file is data-entry work, and removing a file from an
 * audit is done by changing the audit's data scope.
 */
import { Note, SideDrawer, StatusBadge } from '../../shared/ui';
import { useFileEventsQuery } from '../../queries/intake';
import { bytes, count, dateTime, percent } from '../../utils/format';
import { ErrorState } from '../shared/states';
import { DataTable, type Column } from '../shared/tables';
import type { FileRecord } from '../../schemas';

const CLASS_ORDER = ['SALES', 'RETURNS', 'ADJUSTMENT', 'UNKNOWN'] as const;

export function FileDetailsDrawer({
	file,
	onClose,
}: {
	file: FileRecord | null;
	onClose: () => void;
}) {
	const events = useFileEventsQuery(file?.pkFileId ?? null);
	if (!file) return null;

	const counts: Record<(typeof CLASS_ORDER)[number], number> = {
		SALES: file.classifiedSales ?? 0,
		RETURNS: file.classifiedReturns ?? 0,
		ADJUSTMENT: file.classifiedAdjust ?? 0,
		UNKNOWN: file.classifiedUnknown ?? 0,
	};
	type Row = { key: (typeof CLASS_ORDER)[number]; rows: number };
	const columns: Array<Column<Row>> = [
		{
			key: 'class',
			label: 'Classification',
			width: 180,
			render: (row) => (
				<StatusBadge group="classification" value={row.key} />
			),
		},
		{
			key: 'rows',
			label: 'Rows',
			num: true,
			width: 120,
			render: (row) => count(row.rows),
		},
		{
			key: 'share',
			label: 'Share',
			num: true,
			width: 100,
			render: (row) =>
				file.rowCount
					? percent((row.rows / file.rowCount) * 100, 1)
					: '—',
		},
	];

	return (
		<SideDrawer
			open
			onClose={onClose}
			title="File details"
			subtitle={file.originalName}
		>
			<div className="stack">
				{file.excludedReason && (
					<Note tone="warn" title="Excluded">
						{file.excludedReason}
					</Note>
				)}

				<section>
					<h4>File</h4>
					<dl className="kv">
						<dt>Status</dt>
						<dd>
							<StatusBadge
								group="fileStatus"
								value={file.status}
							/>
						</dd>
						<dt>Period</dt>
						<dd>{file.periodLabel ?? '—'}</dd>
						<dt>Rows</dt>
						<dd>
							{file.rowCount === null
								? 'Not read yet'
								: count(file.rowCount)}
						</dd>
						<dt>Size</dt>
						<dd>{bytes(file.sizeBytes)}</dd>
						<dt>Uploaded</dt>
						<dd>{dateTime(file.createdAt)}</dd>
					</dl>
				</section>

				<section>
					<h4>How it was read</h4>
					<dl className="kv">
						<dt>Sheet</dt>
						<dd>{file.sheetName ?? '—'}</dd>
						<dt>Header row</dt>
						<dd>{file.headerRow ?? '—'}</dd>
						<dt>Columns mapped</dt>
						<dd>
							{count(file.columnsMapped)} of{' '}
							{count(file.columnsTotal)}
						</dd>
					</dl>
				</section>

				{file.module !== 'H2R' && (
					<section>
						<h4>Rows by classification</h4>
						<DataTable
							rows={CLASS_ORDER.map((key) => ({
								key,
								rows: counts[key],
							}))}
							columns={columns}
							rowKey={(row) => row.key}
							caption="Rows by classification"
							maxHeight="short"
							totals={{
								class: 'Total',
								rows: count(file.rowCount),
								share: file.rowCount ? '100%' : '—',
							}}
						/>
					</section>
				)}

				<section>
					<h4>History</h4>
					<ErrorState error={events.error} onRetry={events.refetch} />
					{events.isPending ? (
						<p className="mut">Loading…</p>
					) : (events.data ?? []).length === 0 ? (
						<p className="mut">Nothing recorded yet.</p>
					) : (
						<div className="feed">
							{(events.data ?? []).map((event) => (
								<div key={event.pkFileEventId}>
									<div className="b">{event.event}</div>
									{event.note && (
										<div className="small mut">
											{event.note}
										</div>
									)}
									<div className="small mut">
										{dateTime(event.createdAt)}
									</div>
								</div>
							))}
						</div>
					)}
				</section>
			</div>
		</SideDrawer>
	);
}
